import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ValidatorFn, Validators } from '@angular/forms';
import {
  CountryConfig,
  DEFAULT_COUNTRY_CODE,
  IdTypeOption,
  PhoneValidationConfig,
  PostalCodeConfig,
  resolveCountryConfig,
  VehicleRegistrationConfig,
} from './country-config';

/**
 * Single source of truth for country-specific behavior across the app (phone/mobile
 * validation, postal code validation & label, ID type options, state label, date format,
 * currency, etc.).
 *
 * The organization's country is loaded exactly once, at app startup, from the existing
 * `Gds/organization-by-base-api` response (see AppComponent.loadNoraPulseFromGds). Call
 * `setCountry(res?.data?.country)` there — do NOT call the org API again from individual
 * pages just to read the country.
 *
 * Components should consume settings from here instead of hard-coding `country === 'IN'`
 * checks, e.g.:
 *
 *   constructor(private readonly globalSettings: GlobalSettingsService) {}
 *   this.form = this.fb.group({
 *     mobile: ['', this.globalSettings.mobileValidators(true)],
 *     postalCode: ['', this.globalSettings.postalCodeValidators(true)],
 *   });
 *   get idTypes() { return this.globalSettings.idTypes; }
 *
 * To add a new country, add an entry to COUNTRY_CONFIGS in country-config.ts — nothing in
 * this service or in consuming components needs to change.
 */
@Injectable({ providedIn: 'root' })
export class GlobalSettingsService {
  private readonly countrySubject = new BehaviorSubject<string>(DEFAULT_COUNTRY_CODE);
  /** Emits the current ISO country code (e.g. "IN") whenever it changes. */
  readonly country$ = this.countrySubject.asObservable();

  private loaded = false;

  get countryCode(): string {
    return this.countrySubject.value;
  }

  /** Full config for the active country, falling back to the generic international config. */
  get settings(): CountryConfig {
    return resolveCountryConfig(this.countryCode);
  }

  get isIndia(): boolean {
    return this.countryCode === 'IN';
  }

  get phoneValidation(): PhoneValidationConfig {
    return this.settings.phoneValidation;
  }

  get postalCodeValidation(): PostalCodeConfig {
    return this.settings.postalCode;
  }

  get postalCodeLabel(): string {
    return this.settings.postalCode.label;
  }

  get vehicleRegistrationValidation(): VehicleRegistrationConfig {
    return this.settings.vehicleRegistration;
  }

  get idTypes(): IdTypeOption[] {
    return this.settings.idTypes;
  }

  get stateLabel(): string {
    return this.settings.stateLabel;
  }

  get taxIdLabel(): string {
    return this.settings.taxIdLabel;
  }

  get dateFormat(): string {
    return this.settings.dateFormat;
  }

  get currency(): { code: string; symbol: string } {
    return this.settings.currency;
  }

  /**
   * Sets the active country from the organization config response. Safe to call multiple
   * times (e.g. re-login) — it only emits when the value actually changes. Accepts the raw
   * API value as-is (e.g. "IN", "in", or "India"). Missing, blank, or non-string
   * values fall back to India.
   */
  setCountry(rawCountryCode: unknown): void {
    this.loaded = true;
    const code = this.normalize(rawCountryCode) ?? DEFAULT_COUNTRY_CODE;
    if (code !== this.countrySubject.value) {
      this.countrySubject.next(code);
    }
  }

  /** Whether the org country has been loaded yet this session. */
  hasLoaded(): boolean {
    return this.loaded;
  }

  mobileValidators(required = false): ValidatorFn[] {
    const validators: ValidatorFn[] = [Validators.pattern(this.phoneValidation.pattern)];
    if (required) validators.unshift(Validators.required);
    return validators;
  }

  postalCodeValidators(required = false): ValidatorFn[] {
    const validators: ValidatorFn[] = [Validators.pattern(this.postalCodeValidation.pattern)];
    if (required) validators.unshift(Validators.required);
    return validators;
  }

  vehicleRegistrationValidators(required = false): ValidatorFn[] {
    const validators: ValidatorFn[] = [Validators.pattern(this.vehicleRegistrationValidation.pattern)];
    if (required) validators.unshift(Validators.required);
    return validators;
  }

  private normalize(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim().toUpperCase();
    if (!trimmed) return null;
    if (trimmed === 'INDIA') return 'IN';
    return trimmed;
  }
}
